import AsyncStorage from '@react-native-async-storage/async-storage';
import { detectOnboardingType } from '../utils/OnboardingTypeDetector';

export interface ABTestConfig {
  key: string;
  version?: number;
  variants: (string | { name: string; weight?: number })[];
  pages: string[];
}

export class ABTestManager {
  private configs: Record<string, ABTestConfig>;
  private loggedTests: Set<string>;
  private keys: Record<string, string>;
  private onboardingType: string;
  private pageTests: Record<string, { name: string; version: number; key: string; variants: any[] }[]>;
  private initialized: boolean = false;

  constructor(testConfigs: Record<string, ABTestConfig> = {}) {
    this.configs = testConfigs;
    this.loggedTests = new Set();
    this.keys = {};
    this.pageTests = {};
    
    // Get current onboarding type for prefixing
    this.onboardingType = detectOnboardingType();
  }

  /**
   * Initialize the manager asynchronously
   * Replaces synchronous constructor logic that relied on synchronous storage
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    
    if (!this.onboardingType) {
      throw new Error('ABTestManager should not be created without a valid onboarding type');
    }
    
    this.validateConfigs();
    await this.initializeKeys();
    this.buildPageTestsMap();
    this.initialized = true;
  }

  /**
   * Get current onboarding type from URL path
   */
  getCurrentOnboardingType() {
    return this.onboardingType;
  }

  /**
   * Validates AB test configurations for common errors
   */
  validateConfigs() {
    const errors: string[] = [];
    
    Object.entries(this.configs).forEach(([name, config]) => {
      // Check if key matches the name pattern
      const expectedKey = `${name}Key`;
      if (config.key !== expectedKey) {
        errors.push(`AB test "${name}" has incorrect key. Expected "${expectedKey}", got "${config.key}"`);
      }

      // Check if variants is an array and not empty
      if (!Array.isArray(config.variants) || config.variants.length === 0) {
        errors.push(`AB test "${name}" must have at least one variant`);
      }

      // Check if pages is an array and not empty
      if (!Array.isArray(config.pages) || config.pages.length === 0) {
        errors.push(`AB test "${name}" must have at least one page`);
      }

      // Check if variants are unique
      const uniqueVariants = new Set(config.variants);
      if (uniqueVariants.size !== config.variants.length) {
        errors.push(`AB test "${name}" has duplicate variants`);
      }
    });

    // Check for duplicate keys
    const keys = Object.values(this.configs).map(config => config.key);
    const uniqueKeys = new Set(keys);
    if (uniqueKeys.size !== keys.length) {
      errors.push('Duplicate keys found in AB test configurations');
    }

    if (errors.length > 0) {
      console.error('AB Test Configuration Errors:', errors);
      if (__DEV__) {
        throw new Error('AB Test Configuration Validation Failed:\n' + errors.join('\n'));
      }
    }
  }

  /**
   * Generates or retrieves variant keys for all tests
   */
  async initializeKeys() {
    const entries = Object.entries(this.configs);
    for (const [name, config] of entries) {
      // This will throw naturally if there's an error - ErrorReporter will catch it
      this.keys[`${name}Key`] = await this.getKey(config.key, config.variants);
    }
  }

  /**
   * Gets or generates a variant key for a specific test
   */
  async getKey(storageKey: string, variants: any[]): Promise<string> {
    // Add onboarding prefix to avoid conflicts
    const prefixedKey = `${this.onboardingType}_${storageKey}`;
    
    // Get stored variant, returns undefined if key doesn't exist or storage fails
    let key: string | undefined;
    try {
      const item = await AsyncStorage.getItem(prefixedKey);
      key = item ? JSON.parse(item) : undefined;
    } catch (e) {
      // Storage read failed, continue with undefined
      key = undefined;
    }
    
    // Extract variant names for comparison and selection
    const variantNames = variants.map(v => typeof v === 'object' ? v.name : v);
    
    if (!key || !variantNames.includes(key)) {
      // Generate new variant using weighted selection if applicable
      const selectedVariant = this.selectWeightedVariant(variants);
      key = typeof selectedVariant === 'object' ? selectedVariant.name : selectedVariant;
      // Set storage, fails silently if storage is blocked
      try {
        await AsyncStorage.setItem(prefixedKey, JSON.stringify(key));
      } catch (e) {
        // Storage write failed, continue anyway
      }
    }
    
    // key is guaranteed to be a string here (either from storage or newly generated)
    return key as string;
  }

  /**
   * Selects a variant using weighted random selection if weights are present
   */
  selectWeightedVariant(variants: any[]) {
    // Check if any variant has weight property
    const hasWeights = variants.some(v => typeof v === 'object' && v.weight !== undefined);
    
    if (!hasWeights) {
      // Simple random selection for string variants
      return variants[Math.floor(Math.random() * variants.length)];
    }
    
    // Weighted selection
    const totalWeight = variants.reduce((sum, v) => sum + (v.weight || 0), 0);
    let random = Math.random() * totalWeight;
    
    for (const variant of variants) {
      random -= (variant.weight || 0);
      if (random <= 0) {
        return variant;
      }
    }
    
    // Fallback to first variant
    return variants[0];
  }

  /**
   * Builds a map of pages to their associated AB tests
   */
  buildPageTestsMap() {
    this.pageTests = Object.entries(this.configs).reduce((acc: any, [name, config]) => {
      config.pages.forEach(page => {
        if (!acc[page]) acc[page] = [];
        acc[page].push({
          name,
          version: config.version || 1, // Default to version 1 if not specified
          key: this.keys[`${name}Key`],
          variants: config.variants
        });
      });
      return acc;
    }, {});
  }

  /**
   * Gets AB tests that should be logged for a specific page
   */
  getABTests(pageKey: string) {
    // Return empty array if pageKey is undefined
    if (!pageKey) {
      return [];
    }

    // Get tests for the current page
    const pageTests = this.pageTests[pageKey] || [];
    
    // Filter tests that have multiple variants and haven't been logged yet
    return pageTests
      .filter(test => test.variants.length > 1)
      .map(test => {
        // Include version in analytics log name if version > 1
        const versionSuffix = test.version > 1 ? `-v${test.version}` : '';
        return `${test.name}${versionSuffix}_${test.key}`;
      })
      .filter(test => {
        if (this.loggedTests.has(test)) {
          return false;
        }
        this.loggedTests.add(test);
        return true;
      });
  }

  /**
   * Gets the current variant for a specific test
   */
  async getVariant(testName: string) {
    if (!this.initialized) await this.initialize();
    
    const test = this.configs[testName];
    if (!test) {
      console.error(`AB test "${testName}" not found. Available tests: ${Object.keys(this.configs).join(', ')}`);
      return null;
    }

    return this.keys[`${testName}Key`];
  }

  /**
   * Changes the variant for a specific test
   */
  async changeVariant(testName: string, variant: string) {
    if (!this.initialized) await this.initialize();

    const test = this.configs[testName];
    if (!test) {
      console.error(`AB test "${testName}" not found. Available tests: ${Object.keys(this.configs).join(', ')}`);
      return;
    }

    // Extract variant names for comparison
    const variantNames = test.variants.map(v => typeof v === 'object' ? v.name : v);
    
    if (!variantNames.includes(variant)) {
      console.error(`Variant "${variant}" not found for "${testName}". Available variants: ${variantNames.join(', ')}`);
      return;
    }

    // Update using prefixed storage key
    const prefixedKey = `${this.onboardingType}_${test.key}`;
    try {
      await AsyncStorage.setItem(prefixedKey, JSON.stringify(variant));
    } catch (e) {
      console.warn('Failed to save variant to storage:', e);
    }
    
    console.log(`Changed ${testName} to variant: ${variant}`);
    
    // Update internal keys
    this.keys[`${testName}Key`] = variant;
    
    // Rebuild page tests map with new variant
    this.buildPageTestsMap();
  }

  /**
   * Gets all test keys (for backward compatibility)
   */
  getAllKeys() {
    return { ...this.keys };
  }

  /**
   * Gets all configured tests with their current states for debugging
   */
  async getAllTests() {
    if (!this.initialized) await this.initialize();

    return Object.entries(this.configs).map(([name, config]) => {
      const currentVariant = this.keys[`${name}Key`];
      const variantNames = config.variants.map(v => typeof v === 'object' ? v.name : v);
      
      return {
        name,
        key: config.key,
        currentVariant,
        variants: variantNames,
        version: config.version || 1
      };
    });
  }
}

/**
 * Factory function to create an AB test manager
 */
export const createABTestManager = (testConfigs: Record<string, ABTestConfig>) => {
  return new ABTestManager(testConfigs);
};

import { abTestConfigs } from '../config/abTests';

// Pre-configured instance for app usage
export const abTestManager = createABTestManager(abTestConfigs);
